import dayjs from "dayjs";
import { useCallback } from "react";

import { useTimezone } from "@web-app/api";
import { Order } from "@web-app/types";

export interface ValidationState {
  isValid: boolean;
  message?: string;
}

export function useRouteValidation(
  editRouteOrderIds: string[],
  orders: Map<string, Order> | undefined,
  estimatedPickupTime?: Date | null,
) {
  const { convertToTimezone } = useTimezone();
  const getAssigneeValidationState = useCallback((): ValidationState => {
    if (editRouteOrderIds.length === 0) return { isValid: true };

    const ordersWithAssignees = editRouteOrderIds
      .map((id) => orders?.get(id))
      .filter((order): order is Order => !!order);

    const hasAssignedOrders = ordersWithAssignees.some(
      (order) => !!order.assigneeId,
    );
    const hasUnassignedOrders = ordersWithAssignees.some(
      (order) => !order.assigneeId,
    );
    const assignedOrderIds = ordersWithAssignees
      .filter((order) => order.assigneeId)
      .map((order) => order.assigneeId);
    const uniqueAssignees = new Set(assignedOrderIds);

    if (hasAssignedOrders && hasUnassignedOrders) {
      return {
        isValid: false,
        message:
          "Some orders have assignees while others don't. Please ensure all orders are either assigned to the same person or unassigned.",
      };
    }

    if (uniqueAssignees.size > 1) {
      return {
        isValid: false,
        message:
          "Orders have different assignees. Please ensure all orders are assigned to the same person.",
      };
    }

    return { isValid: true };
  }, [editRouteOrderIds, orders]);

  const isAssigneesValid = useCallback(() => {
    return getAssigneeValidationState().isValid;
  }, [getAssigneeValidationState]);

  const isRouteSequenceValid = useCallback((): ValidationState => {
    if (editRouteOrderIds.length === 0) return { isValid: true };

    const validOrders = editRouteOrderIds
      .map((id) => orders?.get(id))
      .filter((order): order is Order => !!order);

    // Check if first order is a pickup
    const firstOrder = validOrders[0];
    if (!firstOrder || firstOrder.type !== "ROUTE_OPT_PICKUP") {
      return {
        isValid: false,
        message: "Route must start with a pickup order.",
      };
    }

    // Check if there's at least one dropoff order
    const hasDropoff = validOrders.some(
      (order) => order.type === "ROUTE_OPT_DROPOFF",
    );
    if (!hasDropoff) {
      return {
        isValid: false,
        message: "Route must contain at least one dropoff order.",
      };
    }

    return { isValid: true };
  }, [editRouteOrderIds, orders]);

  const isWorkspaceValid = useCallback((): ValidationState => {
    if (editRouteOrderIds.length === 0) return { isValid: true };

    const validOrders = editRouteOrderIds
      .map((id) => orders?.get(id))
      .filter((order): order is Order => !!order);

    // Get the first order's workspace IDs as reference
    const firstOrder = validOrders[0];
    const referenceWorkspaceId = firstOrder.workspaceId;

    // Normalize connectedWorkspaceId: treat falsy values and self-referencing
    // (connectedWorkspaceId === workspaceId) as null, since both mean "not connected"
    const normalizeConnectedWsId = (order: Order) => {
      const val = order.connectedWorkspaceId || null;
      return val === order.workspaceId ? null : val;
    };

    const referenceConnectedWorkspaceId = normalizeConnectedWsId(firstOrder);

    // Both workspaceId AND connectedWorkspaceId must match across all orders
    const hasDifferentWorkspaces = validOrders.some((order) => {
      if (order.workspaceId !== referenceWorkspaceId) return true;
      if (normalizeConnectedWsId(order) !== referenceConnectedWorkspaceId)
        return true;
      return false;
    });

    if (hasDifferentWorkspaces) {
      return {
        isValid: false,
        message: "All orders must belong to the same workspace.",
      };
    }

    return { isValid: true };
  }, [editRouteOrderIds, orders]);

  const isSameDateOrders = useCallback((): ValidationState => {
    if (editRouteOrderIds.length === 0) return { isValid: true };

    const validOrders = editRouteOrderIds
      .map((id) => orders?.get(id))
      .filter((order): order is Order => !!order);

    if (validOrders.some((order) => !order.timezone)) {
      // NOTE: FIXME: This should not happen as timezone is required for orders. But bypassing it temporarily to avoid blocking users.
      return { isValid: true };
    }

    const firstOrder = validOrders[0];
    const firstOrderDate = firstOrder?.scheduledStartDateTime;
    const firstOrderTimezone = firstOrder?.timezone;

    const firstOrderActualDate = convertToTimezone(
      new Date(firstOrderDate),
      firstOrderTimezone!,
    );

    const hasDifferentDates = validOrders.slice(1).some((order) => {
      const orderDate = order?.scheduledStartDateTime;
      const orderTimezone = order?.timezone;

      const orderActualDate = convertToTimezone(
        new Date(orderDate),
        orderTimezone!,
      );

      return (
        dayjs(firstOrderActualDate)
          .tz(firstOrderTimezone)
          .isSame(dayjs(orderActualDate).tz(orderTimezone!), "date") === false
      );
    });

    if (hasDifferentDates) {
      return {
        isValid: false,
        message: "All orders must be scheduled for the same date.",
      };
    }

    return { isValid: true };
  }, [editRouteOrderIds, orders]);

  const isPickupTimeValid = useCallback((): ValidationState => {
    if (!estimatedPickupTime || editRouteOrderIds.length === 0) {
      return { isValid: true };
    }

    const firstOrder = orders?.get(editRouteOrderIds[0]);
    if (!firstOrder) {
      return { isValid: true };
    }

    const scheduledStart = new Date(firstOrder.scheduledStartDateTime);
    scheduledStart.setSeconds(0, 0);
    const scheduledEnd = new Date(firstOrder.scheduledEndDateTime);
    scheduledEnd.setSeconds(0, 0);

    if (
      estimatedPickupTime < scheduledStart ||
      estimatedPickupTime > scheduledEnd
    ) {
      return {
        isValid: false,
        message: "Estimated pickup time should be within pickup time window",
      };
    }

    return { isValid: true };
  }, [estimatedPickupTime, editRouteOrderIds, orders]);

  return {
    getAssigneeValidationState,
    isAssigneesValid,
    isPickupTimeValid,
    isRouteSequenceValid,
    isSameDateOrders,
    isWorkspaceValid,
  };
}
